import React, { useEffect, useMemo, useState } from "react";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch } from "react-redux";
import PageHeaderLayout from "../../components/Layout/PageHeaderLayout";
import { usePermission } from "../../hooks/usePermission";
import { QuickBookService } from "./Service";
import { optionId, optionLabel } from "./catalogModel";

const listItems = (items = []) => items.map((value) => ({ value }));

export const QUICK_BOOK_DEFAULTS = {
  serviceMasterId: null,
  serviceBrandId: null,
  mode: "FIXED_SERVICE",
  customerTitle: "",
  customerSummary: "",
  includedItems: [{ value: "" }],
  excludedItems: [{ value: "" }],
  preparationItems: [],
  inspectionDisclaimer: "",
  unitLabel: "unit",
  unitAmountRupees: "",
  pricingModel: "GST_COMPLIANT_V2",
  taxLiabilityMode: "ECO_9_5",
  offerUnitAmountRupees: "",
  offerActive: false,
  unitDurationMinutes: 45,
  bufferMinutes: 15,
  maxQuantity: 1,
  primaryOfferTtlSeconds: 120,
  allowPayNow: true,
  allowPayAtService: true,
  defaultPaymentChoice: "PAY_NOW",
  status: "DRAFT",
  displayOrder: 0,
};

export const normalizeQuickBookMode = (values) => values.mode === "INSPECTION_FIRST"
  ? {
      ...values,
      maxQuantity: 1,
      allowPayNow: true,
      allowPayAtService: false,
      defaultPaymentChoice: "PAY_NOW",
    }
  : values;

export const normalizeQuickBookCommercialModel = (values) => values.pricingModel === "GST_COMPLIANT_V2"
  ? values
  : { ...values, offerActive: false };

export const isQuickBookServiceReadOnly = ({ isNew, persistedStatus, version }) => (
  !isNew && (persistedStatus === "ARCHIVED" || version === null)
);

export const toQuickBookServicePayload = (rawValues, expectedVersion) => {
  const values = normalizeQuickBookCommercialModel(normalizeQuickBookMode(rawValues));
  const payload = {
    serviceMasterId: Number(values.serviceMasterId),
    ...(values.serviceBrandId ? { serviceBrandId: Number(values.serviceBrandId) } : {}),
    mode: values.mode,
    customerTitle: values.customerTitle.trim(),
    customerSummary: values.customerSummary.trim(),
    includedItems: values.includedItems.map((item) => item.value.trim()).filter(Boolean),
    excludedItems: values.excludedItems.map((item) => item.value.trim()).filter(Boolean),
    preparationItems: values.preparationItems.map((item) => item.value.trim()).filter(Boolean),
    inspectionDisclaimer: values.inspectionDisclaimer?.trim() || null,
    unitLabel: values.unitLabel.trim(),
    unitAmountPaise: Math.round(Number(values.unitAmountRupees) * 100),
    pricingModel: values.pricingModel || "LEGACY_FIXED_PAYOUT",
    taxLiabilityMode: values.taxLiabilityMode || "ECO_9_5",
    offerUnitAmountPaise: values.offerUnitAmountRupees === "" || values.offerUnitAmountRupees === null || values.offerUnitAmountRupees === undefined
      ? null
      : Math.round(Number(values.offerUnitAmountRupees) * 100),
    offerActive: Boolean(values.offerActive),
    unitDurationMinutes: Number(values.unitDurationMinutes),
    bufferMinutes: Number(values.bufferMinutes),
    maxQuantity: Number(values.maxQuantity),
    primaryOfferTtlSeconds: Number(values.primaryOfferTtlSeconds),
    allowPayNow: Boolean(values.allowPayNow),
    allowPayAtService: Boolean(values.allowPayAtService),
    defaultPaymentChoice: values.defaultPaymentChoice,
    status: values.status,
    displayOrder: Number(values.displayOrder),
  };
  return expectedVersion ? { ...payload, expectedVersion } : payload;
};

const plainTextRule = (max) => (value) => {
  const text = String(value || "").trim();
  if (!text) return "Required";
  if (text.length > max) return `Maximum ${max} characters`;
  const unsafe = /[<>]/.test(text) || Array.from(text).some((character) => {
    const code = character.charCodeAt(0);
    return (code < 32 && ![9, 10, 13].includes(code)) || code === 127;
  });
  return !unsafe || "Plain text only";
};

const Section = ({ title, children }) => (
  <Card variant="outlined" sx={{ p: { xs: 1.5, md: 2 } }}>
    <Typography component="h2" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
    <Divider sx={{ mb: 2 }} />
    {children}
  </Card>
);

const ScopeList = ({ control, name, label, required = false }) => {
  const { fields, append, remove, move } = useFieldArray({ control, name });
  return (
    <Box>
      <Typography sx={{ fontSize: ".8rem", fontWeight: 600, mb: 0.5 }}>{label}</Typography>
      <Stack spacing={1}>
        {fields.map((item, index) => (
          <Box key={item.id} sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
            <Controller
              name={`${name}.${index}.value`}
              control={control}
              rules={{ validate: plainTextRule(200) }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label={`${label} ${index + 1}`}
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  inputProps={{ maxLength: 200 }}
                />
              )}
            />
            <Tooltip title="Move up">
              <span><IconButton aria-label={`Move ${label} item ${index + 1} up`} size="small" disabled={index === 0} onClick={() => move(index, index - 1)}><ArrowUpwardIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title="Move down">
              <span><IconButton aria-label={`Move ${label} item ${index + 1} down`} size="small" disabled={index === fields.length - 1} onClick={() => move(index, index + 1)}><ArrowDownwardIcon fontSize="small" /></IconButton></span>
            </Tooltip>
            <Tooltip title="Remove">
              <span><IconButton aria-label={`Remove ${label} item ${index + 1}`} color="error" size="small" disabled={required && fields.length === 1} onClick={() => remove(index)}><DeleteIcon fontSize="small" /></IconButton></span>
            </Tooltip>
          </Box>
        ))}
      </Stack>
      <Button type="button" size="small" onClick={() => append({ value: "" })} disabled={fields.length >= 20} sx={{ mt: 1 }}>
        Add item
      </Button>
    </Box>
  );
};

const numberRules = (min, max) => ({
  required: "Required",
  min: { value: min, message: `Minimum ${min}` },
  max: { value: max, message: `Maximum ${max}` },
  validate: (value) => Number.isInteger(Number(value)) || "Whole numbers only",
});

const ok = (response) => response?.success === 1 || response?.success === "1";

const money = (paise) => new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 2,
}).format(Number(paise || 0) / 100);

const taxProfileFor = (service) => service?.taxProfile
  || service?.gstDetails
  || service?.gst_details
  || service?.canonicalService?.gstDetails
  || service?.canonicalService?.gst_details
  || null;

const taxProfileText = (service) => {
  const profile = taxProfileFor(service);
  if (!profile) return "Tax profile will be validated by the backend";
  const sac = profile.sac || profile.sacCode || profile.code;
  const rate = profile.totalRatePercent ?? profile.gstRatePercent ?? profile.igst;
  return [sac ? `SAC ${sac}` : null, Number.isFinite(Number(rate)) ? `GST ${Number(rate)}%` : null]
    .filter(Boolean)
    .join(" · ") || "Tax profile linked";
};

export function CommercialSettingsDialog({ open, settings, onClose, onSaved, onReloaded }) {
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const { control, handleSubmit, reset, setError } = useForm({
    mode: "onChange",
    defaultValues: { operatorState: "", commissionGstRatePercent: "18" },
  });

  useEffect(() => {
    if (!open) return;
    setMessage(null);
    reset({
      operatorState: settings?.operatorState || "",
      commissionGstRatePercent: String(settings?.commissionGstRatePercent ?? 18),
    });
  }, [open, reset, settings]);

  const submit = async (values) => {
    setSaving(true);
    setMessage(null);
    const response = await QuickBookService.updateCommercialSettings({
      expectedVersion: settings?.version ?? 0,
      operatorState: values.operatorState.trim(),
      commissionGstRatePercent: Number(values.commissionGstRatePercent),
    });
    if (ok(response)) {
      setSaving(false);
      onSaved(response.data);
      return;
    }
    if (response?.error?.code === "QUICK_BOOK_CONFIG_VERSION_CHANGED") {
      const latestResponse = await QuickBookService.commercialSettings();
      setSaving(false);
      if (ok(latestResponse)) {
        const latest = latestResponse.data;
        reset({
          operatorState: latest.operatorState || "",
          commissionGstRatePercent: String(latest.commissionGstRatePercent ?? 18),
        });
        onReloaded?.(latest);
        setMessage({ severity: "warning", text: "Commercial settings changed in another session. The latest values are loaded; review them before saving again." });
        return;
      }
      setMessage({ severity: "error", text: latestResponse?.message || "Settings changed, but the latest values could not be loaded. Close and reopen this page before retrying." });
      return;
    }
    setSaving(false);
    (response?.error?.fieldErrors || []).forEach(({ field, message: fieldMessage }) => {
      setError(field, { type: "server", message: fieldMessage });
    });
    setMessage({ severity: response?.error?.retryable ? "warning" : "error", text: response?.message || "Commercial settings could not be saved." });
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" aria-labelledby="commercial-settings-title">
      <DialogTitle id="commercial-settings-title">Quick Book commercial settings</DialogTitle>
      <DialogContent dividers>
        <Box component="form" id="commercial-settings-form" onSubmit={handleSubmit(submit)} noValidate>
          <Stack spacing={2}>
            {message && <Alert severity={message.severity}>{message.text}</Alert>}
            <Alert severity="info">These system-wide values apply to every GST-compliant Quick Book offering. Service GST still comes from the canonical service SAC profile.</Alert>
            <Controller
              name="operatorState"
              control={control}
              rules={{ required: "Required", pattern: { value: /^\d{2}$/, message: "Use a 2-digit GST state code" } }}
              render={({ field, fieldState }) => <TextField {...field} fullWidth label="CuDel GST state code" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "For example, 09 for Uttar Pradesh"} inputProps={{ maxLength: 2, inputMode: "numeric" }} />}
            />
            <Controller
              name="commissionGstRatePercent"
              control={control}
              rules={{ required: "Required", min: { value: 0, message: "Minimum 0" }, max: { value: 100, message: "Maximum 100" }, validate: (value) => /^\d+(\.\d{1,2})?$/.test(String(value)) || "Use at most 2 decimal places" }}
              render={({ field, fieldState }) => <TextField {...field} type="number" fullWidth label="Commission GST rate (%)" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Applied to CuDel's per-unit commission"} inputProps={{ min: 0, max: 100, step: "0.01" }} />}
            />
            <Typography color="text.secondary" sx={{ fontSize: ".75rem" }}>Contract {settings?.pricingContractVersion || "1.2.0"} · settings version {settings?.version ?? "not configured"}</Typography>
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button type="submit" form="commercial-settings-form" variant="contained" disabled={saving}>{saving ? <CircularProgress size={20} /> : "Save settings"}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function QuickBookServiceForm() {
  const { serviceId } = useParams();
  const isNew = !serviceId;
  const permission = usePermission("service");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [version, setVersion] = useState(null);
  const [persistedStatus, setPersistedStatus] = useState(null);
  const [canonicalOptions, setCanonicalOptions] = useState([]);
  const [canonicalSearch, setCanonicalSearch] = useState("");
  const [canonicalLoading, setCanonicalLoading] = useState(false);
  const [canonicalFallback, setCanonicalFallback] = useState(null);
  const [brandOptions, setBrandOptions] = useState([]);
  const [brandFallback, setBrandFallback] = useState(null);
  const [brandLoading, setBrandLoading] = useState(false);
  const [brandError, setBrandError] = useState("");
  const [commercialSettings, setCommercialSettings] = useState(null);
  const [commercialSettingsLoading, setCommercialSettingsLoading] = useState(true);
  const [commercialSettingsError, setCommercialSettingsError] = useState("");
  const [commercialSettingsOpen, setCommercialSettingsOpen] = useState(false);
  const {
    control,
    handleSubmit,
    reset,
    setError,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange", defaultValues: QUICK_BOOK_DEFAULTS });

  const mode = watch("mode");
  const serviceMasterId = watch("serviceMasterId");
  const allowPayNow = watch("allowPayNow");
  const allowPayAtService = watch("allowPayAtService");
  const pricingModel = watch("pricingModel");
  const offerActive = watch("offerActive");
  const unitAmountRupees = watch("unitAmountRupees");
  const offerUnitAmountRupees = watch("offerUnitAmountRupees");

  useEffect(() => {
    dispatch({ type: "menuSelect", payload: "quick-book-services" });
  }, [dispatch]);

  useEffect(() => {
    let active = true;
    setCommercialSettingsLoading(true);
    setCommercialSettingsError("");
    QuickBookService.commercialSettings().then((response) => {
      if (!active) return;
      if (ok(response)) setCommercialSettings(response.data);
      else setCommercialSettingsError(response?.message || "Commercial settings could not be loaded.");
      setCommercialSettingsLoading(false);
    }).catch(() => {
      if (!active) return;
      setCommercialSettingsError("Commercial settings could not be loaded.");
      setCommercialSettingsLoading(false);
    });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (mode === "INSPECTION_FIRST") {
      setValue("maxQuantity", 1, { shouldValidate: true });
      setValue("allowPayNow", true, { shouldValidate: true });
      setValue("allowPayAtService", false, { shouldValidate: true });
      setValue("defaultPaymentChoice", "PAY_NOW", { shouldValidate: true });
    }
  }, [mode, setValue]);

  useEffect(() => {
    if (!allowPayNow && allowPayAtService) setValue("defaultPaymentChoice", "PAY_AT_SERVICE");
    if (allowPayNow && !allowPayAtService) setValue("defaultPaymentChoice", "PAY_NOW");
  }, [allowPayAtService, allowPayNow, setValue]);

  useEffect(() => {
    if (pricingModel !== "GST_COMPLIANT_V2" && offerActive) {
      setValue("offerActive", false, { shouldDirty: true, shouldValidate: true });
    }
  }, [offerActive, pricingModel, setValue]);

  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      setCanonicalLoading(true);
      const response = await QuickBookService.canonicalServices(canonicalSearch.trim());
      if (active && (response?.success === 1 || response?.success === "1")) {
        setCanonicalOptions((response.data?.list || []).filter((item) => Number(item.parentServiceId) > 0));
      }
      if (active) setCanonicalLoading(false);
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [canonicalSearch]);

  useEffect(() => {
    if (!serviceMasterId) {
      setBrandOptions([]);
      setBrandError("");
      return;
    }
    let active = true;
    setBrandLoading(true);
    setBrandError("");
    QuickBookService.canonicalOptions({
      mode,
      parentSourceType: "CHILD_SERVICE",
      parentSourceId: serviceMasterId,
      sourceType: "BRAND",
    }).then((response) => {
      if (!active) return;
      if (response?.success === 1 || response?.success === "1") {
        const data = response.data;
        setBrandOptions(Array.isArray(data) ? data : data?.items || data?.options || []);
      } else {
        setBrandOptions([]);
        setBrandError(response?.message || "Brand options could not be loaded.");
      }
      setBrandLoading(false);
    }).catch(() => {
      if (!active) return;
      setBrandOptions([]);
      setBrandError("Brand options could not be loaded.");
      setBrandLoading(false);
    });
    return () => { active = false; };
  }, [mode, serviceMasterId]);

  useEffect(() => {
    if (isNew) return;
    let active = true;
    const load = async () => {
      setLoading(true);
      const response = await QuickBookService.detail(serviceId);
      if (!active) return;
      if (response?.success === 1 || response?.success === "1") {
        const data = response.data;
        const formValues = {
          ...QUICK_BOOK_DEFAULTS,
          ...data,
          unitAmountRupees: (Number(data.unitAmountPaise || 0) / 100).toFixed(2),
          pricingModel: data.pricingModel || "LEGACY_FIXED_PAYOUT",
          taxLiabilityMode: data.taxLiabilityMode || "ECO_9_5",
          offerUnitAmountRupees: data.offerUnitAmountPaise === null || data.offerUnitAmountPaise === undefined
            ? ""
            : (Number(data.offerUnitAmountPaise) / 100).toFixed(2),
          offerActive: Boolean(data.offerActive),
          includedItems: listItems(data.includedItems),
          excludedItems: listItems(data.excludedItems),
          preparationItems: listItems(data.preparationItems),
          inspectionDisclaimer: data.inspectionDisclaimer || "",
        };
        reset(formValues);
        setVersion(data.version);
        setPersistedStatus(data.status);
        setCanonicalFallback(data.canonicalService
          ? { ...data.canonicalService, taxProfile: data.taxProfile || data.canonicalService.taxProfile }
          : { id: data.serviceMasterId, title: `Service #${data.serviceMasterId}`, taxProfile: data.taxProfile });
        setBrandFallback(data.serviceBrand || (data.serviceBrandId ? { id: data.serviceBrandId, title: data.brandTitle || `Brand #${data.serviceBrandId}` } : null));
      } else {
        setVersion(null);
        setPersistedStatus(null);
        setMessage({ severity: "error", text: response?.message || "Quick Book service could not be loaded." });
      }
      setLoading(false);
    };
    load();
    return () => { active = false; };
  }, [isNew, reset, serviceId]);

  const canonicalChoices = useMemo(() => {
    if (!canonicalFallback || canonicalOptions.some((item) => item.id === canonicalFallback.id)) return canonicalOptions;
    return [canonicalFallback, ...canonicalOptions];
  }, [canonicalFallback, canonicalOptions]);
  const brandChoices = useMemo(() => {
    if (!brandFallback || brandOptions.some((item) => optionId(item) === optionId(brandFallback))) return brandOptions;
    return [brandFallback, ...brandOptions];
  }, [brandFallback, brandOptions]);
  const selectedCanonicalService = canonicalChoices.find((item) => Number(item.id) === Number(serviceMasterId))
    || canonicalFallback;

  if ((isNew && !permission.canCreate) || (!isNew && !permission.canEdit)) {
    return <Navigate to="/unauthorized" replace />;
  }

  const onSubmit = async (values) => {
    if (!values.allowPayNow && !values.allowPayAtService) {
      setError("allowPayNow", { type: "validate", message: "Enable at least one payment choice" });
      return;
    }
    if (values.defaultPaymentChoice === "PAY_NOW" && !values.allowPayNow) {
      setError("defaultPaymentChoice", { type: "validate", message: "Default payment must be enabled" });
      return;
    }
    if (values.defaultPaymentChoice === "PAY_AT_SERVICE" && !values.allowPayAtService) {
      setError("defaultPaymentChoice", { type: "validate", message: "Default payment must be enabled" });
      return;
    }
    if (values.offerActive && (values.offerUnitAmountRupees === "" || values.offerUnitAmountRupees === null)) {
      setError("offerUnitAmountRupees", { type: "validate", message: "Enter an offer price before activating it" });
      return;
    }
    if (values.offerActive && Number(values.offerUnitAmountRupees) >= Number(values.unitAmountRupees)) {
      setError("offerUnitAmountRupees", { type: "validate", message: "Offer price must be lower than the regular price" });
      return;
    }
    if (values.status === "ACTIVE" && values.pricingModel === "GST_COMPLIANT_V2" && !commercialSettings) {
      setMessage({ severity: "error", text: "Load and configure the system commercial settings before activating this GST-compliant offering." });
      return;
    }

    setSaving(true);
    setMessage(null);
    const response = isNew
      ? await QuickBookService.create(toQuickBookServicePayload(values))
      : await QuickBookService.update(serviceId, toQuickBookServicePayload(values, version));
    setSaving(false);

    if (response?.success === 1 || response?.success === "1") {
      setMessage({ severity: "success", text: response.message || "Quick Book service saved." });
      if (isNew) {
        navigate(`/admin/quick-book/services/${response.data.serviceId}/edit`, { replace: true });
      } else {
        setVersion(response.data.version);
        setPersistedStatus(response.data.status);
      }
      return;
    }

    (response?.error?.fieldErrors || []).forEach(({ field, message: fieldMessage }) => {
      setError(field, { type: "server", message: fieldMessage });
    });
    setMessage({
      severity: response?.error?.retryable ? "warning" : "error",
      text: response?.message || "Quick Book service could not be saved.",
    });
  };

  if (loading) {
    return <Box sx={{ display: "grid", minHeight: 240, placeItems: "center" }}><CircularProgress /></Box>;
  }

  const archived = !isNew && persistedStatus === "ARCHIVED";
  const readOnly = isQuickBookServiceReadOnly({ isNew, persistedStatus, version });

  return (
    <>
      <PageHeaderLayout headerTitle={isNew ? "Add Quick Book Offering" : "Edit Quick Book Offering"}>
        <Button size="small" startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/quick-book/services")}>Back</Button>
      </PageHeaderLayout>
      {message && <Alert severity={message.severity} sx={{ mb: 1 }}>{message.text}</Alert>}
      {archived && <Alert severity="info" sx={{ mb: 1 }}>Archived Quick Book services are read-only.</Alert>}

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={1.5}>
          <Section title="Identity">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="serviceMasterId"
                  control={control}
                  rules={{ required: "Select a canonical child service" }}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={canonicalChoices}
                      value={canonicalChoices.find((item) => Number(item.id) === Number(field.value)) || null}
                      loading={canonicalLoading}
                      getOptionLabel={(option) => option.hierarchyName || option.title || `Service #${option.id}`}
                      isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                      onInputChange={(_, value, reason) => reason === "input" && setCanonicalSearch(value)}
                      onChange={(_, value) => {
                        field.onChange(value?.id || null);
                        setValue("serviceBrandId", null, { shouldDirty: true });
                        setBrandFallback(null);
                      }}
                      renderInput={(params) => (
                        <TextField {...params} label="Canonical service" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Search existing child services"} />
                      )}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="serviceBrandId"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Autocomplete
                      options={brandChoices}
                      value={brandChoices.find((item) => optionId(item) === Number(field.value)) || null}
                      loading={brandLoading}
                      disabled={!serviceMasterId || readOnly}
                      getOptionLabel={optionLabel}
                      isOptionEqualToValue={(option, value) => optionId(option) === optionId(value)}
                      onChange={(_, value) => field.onChange(value ? optionId(value) : null)}
                      noOptionsText={brandError || "No brands for this canonical service"}
                      renderInput={(params) => <TextField {...params} label="Brand (optional)" error={Boolean(fieldState.error) || Boolean(brandError)} helperText={fieldState.error?.message || brandError || "Leave blank when the offering applies to every brand"} />}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="mode"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field }) => (
                    <TextField {...field} select fullWidth label="Mode" required>
                      <MenuItem value="FIXED_SERVICE">Fixed service</MenuItem>
                      <MenuItem value="INSPECTION_FIRST">Inspection first</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <Controller
                  name="unitLabel"
                  control={control}
                  rules={{ validate: plainTextRule(80) }}
                  render={({ field, fieldState }) => <TextField {...field} fullWidth label="Unit label" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ maxLength: 80 }} />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="customerTitle"
                  control={control}
                  rules={{ validate: plainTextRule(160) }}
                  render={({ field, fieldState }) => <TextField {...field} fullWidth label="Customer title" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ maxLength: 160 }} />}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="customerSummary"
                  control={control}
                  rules={{ validate: plainTextRule(500) }}
                  render={({ field, fieldState }) => <TextField {...field} fullWidth multiline minRows={2} label="Customer summary" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ maxLength: 500 }} />}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Customer scope">
            <Grid container spacing={2}>
              <Grid item xs={12} lg={4}><ScopeList control={control} name="includedItems" label="Included" required /></Grid>
              <Grid item xs={12} lg={4}><ScopeList control={control} name="excludedItems" label="Not included" required /></Grid>
              <Grid item xs={12} lg={4}><ScopeList control={control} name="preparationItems" label="Preparation" /></Grid>
              {mode === "INSPECTION_FIRST" && (
                <Grid item xs={12}>
                  <Controller
                    name="inspectionDisclaimer"
                    control={control}
                    rules={{ validate: plainTextRule(500) }}
                    render={({ field, fieldState }) => <TextField {...field} fullWidth multiline minRows={2} label="Inspection disclaimer" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message} inputProps={{ maxLength: 500 }} />}
                  />
                </Grid>
              )}
            </Grid>
          </Section>

          <Section title="Commercials and GST">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Controller
                  name="pricingModel"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} select fullWidth label="Pricing model" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Existing offerings remain legacy until deliberately converted"}>
                      <MenuItem value="LEGACY_FIXED_PAYOUT">Legacy fixed provider payout</MenuItem>
                      <MenuItem value="GST_COMPLIANT_V2">GST compliant v2</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Controller
                  name="taxLiabilityMode"
                  control={control}
                  rules={{ required: pricingModel === "GST_COMPLIANT_V2" ? "Required" : false }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} select fullWidth label="Service GST liability" required={pricingModel === "GST_COMPLIANT_V2"} disabled={pricingModel !== "GST_COMPLIANT_V2"} error={Boolean(fieldState.error)} helperText={fieldState.error?.message || (field.value === "ECO_9_5" ? "CuDel deposits service GST as the liable e-commerce operator" : "Provider is the supplier; collection method controls TCS treatment")}>
                      <MenuItem value="ECO_9_5">CuDel liable operator (section 9(5))</MenuItem>
                      <MenuItem value="SUPPLIER_THROUGH_ECO">Provider supplier through CuDel</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="unitAmountRupees"
                  control={control}
                  rules={{
                    required: "Required",
                    min: { value: 0, message: "Minimum 0" },
                    validate: (value) => /^\d+(\.\d{1,2})?$/.test(String(value)) || "Use at most 2 decimal places",
                  }}
                  render={({ field, fieldState }) => <TextField {...field} type="number" fullWidth label="Regular price incl. GST (₹)" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Customer-facing price before any CuDel-funded offer"} inputProps={{ min: 0, step: "0.01" }} />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Controller
                  name="offerUnitAmountRupees"
                  control={control}
                  rules={{
                    min: { value: 0, message: "Minimum 0" },
                    validate: (value) => value === "" || /^\d+(\.\d{1,2})?$/.test(String(value)) || "Use at most 2 decimal places",
                  }}
                  render={({ field, fieldState }) => <TextField {...field} type="number" fullWidth label="Offer price incl. GST (₹)" disabled={pricingModel !== "GST_COMPLIANT_V2"} error={Boolean(fieldState.error)} helperText={fieldState.error?.message || "Optional CuDel-funded discounted customer price"} inputProps={{ min: 0, step: "0.01" }} />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3} sx={{ display: "flex", alignItems: "center" }}>
                <Controller
                  name="offerActive"
                  control={control}
                  render={({ field }) => <FormControlLabel control={<Checkbox checked={field.value} onChange={(_, checked) => field.onChange(checked)} disabled={pricingModel !== "GST_COMPLIANT_V2" || !offerUnitAmountRupees} />} label="Offer active" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField fullWidth label="Canonical SAC / service GST" value={taxProfileText(selectedCanonicalService)} InputProps={{ readOnly: true }} helperText="Read-only; inherited from the canonical child service" />
              </Grid>
              {pricingModel === "GST_COMPLIANT_V2" && (
                <Grid item xs={12}>
                  <Alert severity={commercialSettingsError ? "warning" : "info"} action={!commercialSettingsLoading && permission.canEdit ? <Button color="inherit" size="small" onClick={() => setCommercialSettingsOpen(true)}>{commercialSettings ? "Edit settings" : "Configure"}</Button> : null}>
                    {commercialSettingsLoading
                      ? "Loading system commercial settings…"
                      : commercialSettingsError
                        ? `${commercialSettingsError} Draft changes can be saved, but activation is blocked until settings are available.`
                        : `System contract ${commercialSettings.pricingContractVersion || "1.2.0"}: CuDel state ${commercialSettings.operatorState}; commission GST ${commercialSettings.commissionGstRatePercent}%.`}
                  </Alert>
                </Grid>
              )}
              {offerActive && Number(unitAmountRupees) > Number(offerUnitAmountRupees) && (
                <Grid item xs={12}>
                  <Alert severity="success">Customer display: <s>{money(Math.round(Number(unitAmountRupees) * 100))}</s> {money(Math.round(Number(offerUnitAmountRupees) * 100))} · save {money(Math.round((Number(unitAmountRupees) - Number(offerUnitAmountRupees)) * 100))}. The backend calculates the taxable discount, GST and payout.</Alert>
                </Grid>
              )}
            </Grid>
          </Section>

          <Section title="Capacity and fulfilment">
            <Grid container spacing={2}>
              {[
                ["unitDurationMinutes", "Unit duration (min)", 1, 1440],
                ["bufferMinutes", "Final buffer (min)", 0, 1440],
                ["maxQuantity", "Maximum quantity", 1, 99],
                ["primaryOfferTtlSeconds", "Offer TTL (sec)", 1, 86400],
                ["displayOrder", "Display order", 0, 1000000],
              ].map(([name, label, min, max]) => (
                <Grid item xs={12} sm={6} md={2.4} key={name}>
                  <Controller
                    name={name}
                    control={control}
                    rules={numberRules(min, max)}
                    render={({ field, fieldState }) => (
                      <TextField {...field} type="number" fullWidth label={label} required error={Boolean(fieldState.error)} helperText={fieldState.error?.message} disabled={name === "maxQuantity" && mode === "INSPECTION_FIRST"} inputProps={{ min, max, step: 1 }} />
                    )}
                  />
                </Grid>
              ))}
            </Grid>
          </Section>

          <Section title="Payment">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Controller
                  name="allowPayNow"
                  control={control}
                  render={({ field }) => <FormControlLabel control={<Checkbox checked={field.value} onChange={(_, checked) => field.onChange(checked)} disabled={mode === "INSPECTION_FIRST"} />} label="Pay Now" />}
                />
                <Controller
                  name="allowPayAtService"
                  control={control}
                  render={({ field }) => <FormControlLabel control={<Checkbox checked={field.value} onChange={(_, checked) => field.onChange(checked)} disabled={mode === "INSPECTION_FIRST"} />} label="Pay at Service" />}
                />
                {errors.allowPayNow && <Typography color="error" sx={{ fontSize: ".75rem" }}>{errors.allowPayNow.message}</Typography>}
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="defaultPaymentChoice"
                  control={control}
                  rules={{ required: "Required" }}
                  render={({ field, fieldState }) => (
                    <TextField {...field} select fullWidth label="Default payment" required error={Boolean(fieldState.error)} helperText={fieldState.error?.message}>
                      {allowPayNow && <MenuItem value="PAY_NOW">Pay Now</MenuItem>}
                      {allowPayAtService && <MenuItem value="PAY_AT_SERVICE">Pay at Service</MenuItem>}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField fullWidth label="Phase 1 fees" value="Platform ₹0 · Convenience ₹0" InputProps={{ readOnly: true }} />
              </Grid>
            </Grid>
          </Section>

          <Section title="Cancellation disclosure">
            <Alert severity="info">
              Cancel at least 2 hours before the scheduled start: 100% refund/no cancellation fee. Within 2 hours: a 2% cancellation fee is displayed against the final charged total. Expert or CuDel cancellation has no customer fee. Refund status follows the existing manual Operations process; this is display-only.
            </Alert>
          </Section>

          <Section title="Status">
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Controller
                  name="status"
                  control={control}
                  render={({ field }) => (
                    <TextField {...field} select fullWidth label="Status" disabled={isNew || archived}>
                      {(isNew ? ["DRAFT"] : ["DRAFT", "ACTIVE", "PAUSED", "ARCHIVED"]).map((value) => <MenuItem key={value} value={value}>{value}</MenuItem>)}
                    </TextField>
                  )}
                />
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography color="text.secondary" sx={{ fontSize: ".8rem" }}>
                  {isNew ? "New services start as Draft. Add an eligible expert mapping before activation." : `Current configuration version: ${version}`}
                </Typography>
              </Grid>
            </Grid>
          </Section>

          <Box sx={{ display: "flex", flexDirection: { xs: "column-reverse", sm: "row" }, justifyContent: "flex-end", gap: 1, pb: 2 }}>
            <Button type="button" variant="outlined" onClick={() => navigate("/admin/quick-book/services")}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving || readOnly}>
              {saving ? "Saving…" : isNew ? "Create draft" : "Save changes"}
            </Button>
          </Box>
        </Stack>
      </Box>
      <CommercialSettingsDialog
        open={commercialSettingsOpen}
        settings={commercialSettings}
        onClose={() => setCommercialSettingsOpen(false)}
        onSaved={(nextSettings) => {
          setCommercialSettings(nextSettings);
          setCommercialSettingsError("");
          setCommercialSettingsOpen(false);
          setMessage({ severity: "success", text: "System commercial settings saved." });
        }}
        onReloaded={(nextSettings) => {
          setCommercialSettings(nextSettings);
          setCommercialSettingsError("");
        }}
      />
    </>
  );
}
